






























































































































import { Component, Vue, Watch } from "vue-property-decorator";
import PortalInput from "@/components/common/PortalInput.vue";
import PortalSelect from "@/components/common/PortalSelect.vue";
import { PaymentStatus, TransactionType, BalanceEffect } from "@/constants/PaymentConstants";
import SelectOption from "@/components/common/SelectOption";
import TaxObligationService from "@/services/TaxObligationService";
import { UpdateTaxPaymentPayload } from "@/dto/tax/UpdateTaxPaymentPayload";
import { processError } from "@/utils/ComponentUtils";
import Application from "@/state/Application";
import { TaxObligationLedgerRecordDTO } from "@/dto/tax/TaxObligationDTO";
import { getTaxObligationLiabilityTypeLabel } from "@/constants/TaxObligationLiabilityType";
import PortalMoneyInput from "@/components/common/PortalMoneyInput.vue";

@Component({
  components: {
    PortalInput,
    PortalSelect,
    PortalMoneyInput
  }
})
export default class EditTaxPaymentModal extends Vue {
  private taxObligationId: number | null = null;
  private paymentId: number | null = null;
  private initialStatus: PaymentStatus | null = null;
  private ledger: TaxObligationLedgerRecordDTO[] = [];
  private form: any = {
    status: null,
    scheduledDate: null,
    actualDate: null,
    declinedAt: null,
    items: []
  };

  get statusOptions(): SelectOption[] {
    let statuses = Object.values(PaymentStatus);
    
    // If it was already not DRAFT, it can only be SETTLED or DECLINED (or its current status)
    if (this.initialStatus && this.initialStatus !== PaymentStatus.DRAFT) {
      statuses = statuses.filter(s => 
        s === PaymentStatus.SETTLED || 
        s === PaymentStatus.DECLINED || 
        s === this.initialStatus
      );
    }

    return statuses.map(status =>
      SelectOption.builder().title(this.$stringUtils.enumToFormattedSentence(status)).value(status).build()
    );
  }

  get isScheduledDateEditable(): boolean {
    return this.form.status !== PaymentStatus.SETTLED && this.form.status !== PaymentStatus.DECLINED;
  }

  get canEditItems(): boolean {
    return this.initialStatus === PaymentStatus.DRAFT;
  }

  get isValid(): boolean {
    if (!this.form.scheduledDate) return false;
    if (this.form.status === PaymentStatus.SETTLED && !this.form.actualDate) return false;
    if (this.form.status === PaymentStatus.DECLINED && !this.form.declinedAt) return false;
    
    const itemsValid = this.form.items.length > 0 && this.form.items.every((item: any) => !!item.transactionType && (item.amountCents > 0));
    return itemsValid;
  }

  public onTransactionTypeChange(item: any): void {
    item.relatedRecordId = null;
  }

  get today(): string {
    return new Date().toISOString().split('T')[0];
  }

  get availableObligations(): TaxObligationLedgerRecordDTO[] {
    return this.ledger.filter(r => r.transactionType === TransactionType.LIABILITY);
  }

  get availablePenalties(): TaxObligationLedgerRecordDTO[] {
    return this.ledger.filter(r => r.transactionType === TransactionType.PENALTY_ASSESSMENT);
  }

  public getRelatedRecordOptions(item: any): SelectOption[] {
    const records = item.transactionType === TransactionType.OBLIGATION_PAYMENT 
      ? this.availableObligations 
      : this.availablePenalties;

    return records.map(r => {
      const typeLabel = r.liabilityType ? getTaxObligationLiabilityTypeLabel(r.liabilityType) : this.$stringUtils.enumToFormattedSentence(r.transactionType);
      const amount = this.formatCurrency(r.amountCents / 100);
      return SelectOption.builder()
        .title(`${typeLabel} (${this.$dateUtils.formatDateToAmericanFormat(r.createDate)}) - ${amount}`)
        .value(r.id)
        .build();
    });
  }

  public getItemWarning(item: any): string | null {
    if (!item.relatedRecordId || !item.amountCents) return null;

    const record = this.ledger.find(r => r.id === item.relatedRecordId);
    if (!record) return null;

    // Calculate total payments for this record in ledger, EXCLUDING current payment items to get others
    // Actually, we should check how much is left on the record.
    const paymentsForRecord = this.ledger
      .filter(r => r.relatedRecordId === item.relatedRecordId && r.balanceEffect === BalanceEffect.DECREASE && r.paymentId !== this.paymentId && r.paymentStatus !== 'DECLINED')
      .reduce((sum, r) => sum + r.amountCents, 0);
    
    if (paymentsForRecord >= record.amountCents) {
      return this.$t("tax_calendar.warnings.already_paid") as string;
    }

    if (item.amountCents > (record.amountCents - paymentsForRecord)) {
      return this.$t("tax_calendar.warnings.amount_exceeds_balance") as string;
    }

    return null;
  }

  public getTransactionTypeOptions(currentType: TransactionType): SelectOption[] {
    const allowedTypes = [TransactionType.OBLIGATION_PAYMENT, TransactionType.PENALTY_PAYMENT];

    return allowedTypes
      .map(type => 
        SelectOption.builder().title(this.$stringUtils.enumToFormattedSentence(type)).value(type).build()
      );
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  }

  get totalAmountCents(): number {
    return this.form.items.reduce((sum: number, item: any) => sum + (item.amountCents || 0), 0);
  }

  public addItem(): void {
    if (this.canAddItem) {
      this.form.items.push({
        transactionType: null,
        amountCents: null,
        relatedRecordId: null
      });
    }
  }

  get canAddItem(): boolean {
    return this.canEditItems;
  }

  public beforeOpen(event: any): void {
    this.taxObligationId = event.params.taxObligationId;
    this.ledger = event.params.ledger || [];
    const payment = event.params.payment;
    this.paymentId = payment.paymentId;
    this.initialStatus = payment.paymentStatus || null;

    // Filter items related to this payment from ledger
    const paymentItems = this.ledger.filter(r => r.paymentId === this.paymentId);
    
    this.form = {
      status: payment.paymentStatus || null,
      scheduledDate: payment.scheduledPaymentDate ? payment.scheduledPaymentDate.substring(0, 10) : null,
      actualDate: payment.actualPaymentDate ? payment.actualPaymentDate.substring(0, 10) : null,
      declinedAt: payment.declinedAt ? payment.declinedAt.substring(0, 10) : null,
      items: paymentItems.map(item => ({
        transactionType: item.transactionType,
        amountCents: item.amountCents,
        relatedRecordId: item.relatedRecordId
      }))
    };

    if (this.$validator) {
      this.$validator.reset();
    }
  }

  public onStatusChange(status: PaymentStatus): void {
    if (status === PaymentStatus.SETTLED) {
      this.form.actualDate = this.form.scheduledDate;
    } else {
      this.form.actualDate = null;
    }

    if (status !== PaymentStatus.DECLINED) {
      this.form.declinedAt = null;
    }
  }

  public closeModal(): void {
    this.$modal.hide('edit-tax-payment-modal');
  }

  public async submit(): Promise<void> {
    const valid = await this.$validator.validateAll();
    if (!valid || !this.isValid || !this.taxObligationId || !this.paymentId) return;

    const payload: UpdateTaxPaymentPayload = {
      status: this.form.status,
      scheduledDate: this.form.scheduledDate,
      actualDate: this.form.status === PaymentStatus.SETTLED ? this.form.actualDate : null,
      declinedAt: this.form.status === PaymentStatus.DECLINED ? this.form.declinedAt : null,
      items: this.canEditItems ? this.form.items : null
    };

    Application.startLoading();
    try {
      await TaxObligationService.updatePayment(this.taxObligationId, this.paymentId, payload);
      this.$emit('updated');
      this.closeModal();
    } catch (e) {
      processError(e, this);
    } finally {
      Application.stopLoading();
    }
  }
}
